<template>
  <v-dialog
    v-model="showDialog"
    transition="slide-x-transition"
    max-width="600px"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        small
        color="primary"
        class="d-none d-md-inline"
        v-bind="attrs"
        v-on="on"
      >
        {{ $t('jobs.subscribeToJobalert') }}
        <v-icon class="append-icon">mdi-bell</v-icon>
      </v-btn>

      <v-btn small color="primary" class="d-md-none" v-bind="attrs" v-on="on">
        {{ $t('jobs.subscribeToJobalert_short') }}
        <v-icon class="append-icon">mdi-bell</v-icon>
      </v-btn>
    </template>

    <v-card class="jobalert-signup-dialog alternate has--title">
      <v-card-title class="d-flex">
        <h2 class="flex-grow-1">{{ $t('jobalert.headline') }}</h2>
        <div>
          <v-btn
            small
            fab
            class="secondary"
            @click="clearAndClose"
            :title="$t('close')"
          >
            <v-icon class="accent--text">mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>
      <v-card-text>
        <p>
          {{ $t('jobalert.description') }}
        </p>
        <p class="text--disabled text-sm">
          {{ $t('application.mandatoryFieldInfo') }}
        </p>

        <v-form ref="form" v-model="isValid" lazy-validation>
          <div class="mb-4">
            <v-text-field
              v-model="email"
              :rules="emailRules"
              required
              flat
              outlined
              hide-details="auto"
              autocomplete="email"
              :label="$t('jobalert.emailAddress') + ' *'"
            />
          </div>

          <div v-if="filters.includes('keyword')" class="mb-4">
            <v-text-field
              v-model="keyword"
              flat
              outlined
              hide-details="auto"
              :label="$t('jobalert.keyword')"
            />
          </div>

          <div
            class="mb-4"
            v-if="
              sortedDepartments.length > 0 && filters.includes('departments')
            "
          >
            <v-select
              v-model="selectedDepartments"
              multiple
              chips
              flat
              outlined
              deletable-chips
              :label="$t('jobs.department')"
              hide-details="auto"
              item-value="text"
              :items="sortedDepartments"
              :menu-props="{ bottom: true, offsetY: true }"
            />
          </div>

          <div
            class="mb-4"
            v-if="sortedCategories.length > 0 && filters.includes('category')"
          >
            <v-select
              v-model="selectedCategories"
              multiple
              chips
              flat
              outlined
              deletable-chips
              :label="$t('jobs.category')"
              hide-details="auto"
              item-value="value"
              :items="sortedCategories"
              :menu-props="{ bottom: true, offsetY: true }"
            />
          </div>

          <div
            class="mb-4"
            v-if="
              sortedCareerLevels.length > 0 && filters.includes('career-levels')
            "
          >
            <v-select
              v-model="selectedCareerLevels"
              multiple
              chips
              flat
              outlined
              deletable-chips
              :label="$t('jobs.careerLevel')"
              hide-details="auto"
              item-value="value"
              :aria-readonly="true"
              :items="sortedCareerLevels"
              :menu-props="{
                bottom: true,
                offsetY: true,
                eager: true,
                atach: true
              }"
            />
          </div>

          <div
            class="mb-4"
            v-if="
              sortedContractTypes.length > 0 &&
              filters.includes('contract-types')
            "
          >
            <v-select
              v-model="selectedContractTypes"
              multiple
              chips
              flat
              outlined
              deletable-chips
              :label="$t('jobs.contractType')"
              hide-details="auto"
              item-value="value"
              :items="sortedContractTypes"
              :menu-props="{ bottom: true, offsetY: true }"
            />
          </div>

          <div class="mb-4">
            <v-checkbox
              v-model="acceptPrivacyPolicy"
              hide-details="auto"
              :rules="privacyPolicyRule"
            >
              <template v-slot:label>
                {{ $t('jobalert.acceptPrivacyPolicy') }}
              </template>
            </v-checkbox>

            <div class="text--disabled text-sm mt-1">
              <i18n path="jobalert.viewPrivacyPolicy">
                <router-link
                  v-if="!customDataPrivacyUrl"
                  :to="{ name: 'privacyPolicy' }"
                  >{{ $t('jobalert.viewPrivacyPolicy_linkLabel') }}</router-link
                >

                <a v-else :href="customDataPrivacyUrl" target="_blank">{{
                  $t('jobalert.viewPrivacyPolicy_linkLabel')
                }}</a>
              </i18n>
            </div>
          </div>
        </v-form>
      </v-card-text>
      <v-card-actions class="justify-center justify-md-end">
        <v-btn small class="secondary" @click="clearAndClose">
          {{ $t('cancel') }}
        </v-btn>

        <v-btn small @click="submit">
          {{ $t('submit') }}
          <v-icon small class="append-icon">mdi-send-outline</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { eventBus } from '@/event-bus.js';
import jobalertService from '@/services/JobalertService.js';
import TrackingService from '@/services/TrackingService.js';
export default {
  data() {
    return {
      isValid: true,
      email: '',
      departments: [],
      categories: [],
      careerLevels: [],
      contractTypes: [],
      selectedDepartments: [],
      selectedCategories: [],
      selectedCareerLevels: [],
      selectedContractTypes: [],
      keyword: '',
      acceptPrivacyPolicy: false,
      showDialog: false,
      formStarted: false,
      suppressCloseTracking: false,
      emailRules: [
        (v) => !!v || this.$t('validation.emailInvalid'),
        (v) => /.+@.+\..+/.test(v) || this.$t('validation.emailInvalid')
      ],
      privacyPolicyRule: [
        (v) => v || this.$t('validation.privacyPolicyInvalid')
      ]
    };
  },

  computed: {
    ...mapGetters('jobsModule', {
      locations: 'getLocations'
    }),

    ...mapState('companyModule', {
      slug: 'slug',
      isInternal: 'isInternal',
      customDataPrivacyUrl: 'customDataPrivacyUrl',
      filters: 'jobalertFilters'
    }),

    ...mapState({
      currentLanguage: 'currentLanguage'
    }),

    sortedCareerLevels() {
      return [...this.careerLevels]
        .sort((a, b) =>
          this.localize(a.name).localeCompare(this.localize(b.name))
        )
        .map((x) => {
          return {
            text: this.localize(x.name),
            value: x.id
          };
        });
    },

    sortedDepartments() {
      return [...this.departments]
        .sort((a, b) =>
          this.localize(a.name).localeCompare(this.localize(b.name))
        )
        .map((x) => {
          return {
            text: this.localize(x.name),
            value: x.id
          };
        });
    },

    sortedCategories() {
      return [...this.categories]
        .sort((a, b) =>
          this.localize(a.name).localeCompare(this.localize(b.name))
        )
        .map((x) => {
          return {
            text: this.localize(x.name),
            value: x.id
          };
        });
    },

    sortedContractTypes() {
      return [...this.contractTypes]
        .sort((a, b) =>
          this.localize(a.name).localeCompare(this.localize(b.name))
        )
        .map((x) => {
          return {
            text: this.localize(x.name),
            value: x.id
          };
        });
    },

    sortedLocations() {
      return [...this.locations].sort((a, b) =>
        this.localize(a.name).localeCompare(this.localize(b.name))
      );
    }
  },

  watch: {
    showDialog(showDialog, wasOpen) {
      if (showDialog) {
        this.suppressCloseTracking = false;
        TrackingService.trackJobalertCta();
        return;
      }

      if (wasOpen) {
        if (!this.suppressCloseTracking) {
          TrackingService.trackJobalertClose();
          this.clear();
        }

        this.suppressCloseTracking = false;
      }
    },

    email() {
      if (!this.formStarted && this.email && this.email.length > 0) {
        TrackingService.trackJobalertFormStart();
        this.formStarted = true;
      }
    }
  },

  async mounted() {
    if (this.filters.includes('career-levels')) {
      this.careerLevels = await this.fetchTranslations('careerLevel');
    }

    if (this.filters.includes('category')) {
      this.categories = await this.fetchTranslations('jobportalCategory');
    }

    if (this.filters.includes('contract-types')) {
      this.contractTypes = await this.fetchTranslations('contract');
    }

    if (this.filters.includes('departments')) {
      this.departments = await this.fetchTranslations('departments');
    }
  },

  methods: {
    ...mapActions('companyModule', {
      loadTranslations: 'loadTranslations'
    }),

    async fetchTranslations(translationKey) {
      const translations = await this.loadTranslations({
        slug: this.slug,
        translationKey: translationKey
      });

      const translationItems = [];
      for (const item of Object.keys(translations)) {
        translationItems.push({
          id: item,
          name: translations[item]
        });
      }

      return translationItems;
    },

    validate() {
      this.$refs.form.validate();
    },

    clear() {
      this.$refs.form.reset();
      this.$refs.form.resetValidation();
      this.formStarted = false;
    },

    clearAndClose() {
      this.showDialog = false;
    },

    displaySuccess() {
      this.$emit('success');
    },

    displayError() {
      this.$emit('error');
    },

    async submit() {
      await this.validate();

      if (this.isValid) {
        this.suppressCloseTracking = true;
        this.showDialog = false;

        // Intentionally tracks valid submit attempts before the backend response.
        TrackingService.trackJobalertSubmit();

        const response = await jobalertService.signup({
          slug: this.slug,
          email: this.email,
          isInternal: this.isInternal,
          departments: this.selectedDepartments,
          categories: this.selectedCategories,
          careerLevels: this.selectedCareerLevels,
          contractTypes: this.selectedContractTypes,
          keyword: this.keyword,
          languageKey: this.currentLanguage
        });

        const result = await response.json();
        if (result.success) {
          eventBus.$emit('jobalert-success');
        } else {
          eventBus.$emit('jobalert-error');
        }
      }
    }
  }
};
</script>
<style scoped>
h2 {
  font-size: var(--font-size-lg) !important;
  color: revert;
}
</style>
